/* eslint-disable @typescript-eslint/no-explicit-any */
import { Search } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { forwardRef, HTMLAttributes, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { PromotionDto, promotionsClient, PromotionUpdateResult, PromotionUpsertDto } from '../../../../app/services/api/promotions';
import { itemTagsClient, ItemTagDto } from '../../../../app/services/api/itemTags';
import { client } from '../../../../app/services/api/orderManagementClient';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import BackButton from '../../../../components/shared/actions/BackButton';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import { UserContext } from '../../../../components/shared/useUser';
import { Section } from '../../../../components/ui/Section';

// ─── Types ───────────────────────────────────────────────────────────────────

interface InstanceOption { id: number; name: string; }
interface MarketOption { id: number; name: string; companyId: number; }
interface CatalogueOption { id: number; name: string; }
interface CategoryOption { id: number; name: string; }
interface PromotionProduct {
  contentId: number;
  itemNumber: string;
  productName: string;
  categoryName: string;
}

interface ValidationErrors {
  name?: string;
  contentId?: string;
  catalogueId?: string;
  companyId?: string;
  customerTypeId?: string;
  pricingType?: string;
  discountPercentage?: string;
  fixedPromoUnitPrice?: string;
  quantityCap?: string;
  reservationDurationMinutes?: string;
  startDateUtc?: string;
  endDateUtc?: string;
}

type DurationUnit = 'minutes' | 'hours' | 'days';

// ─── Virtualized table scaffolding ───────────────────────────────────────────

const PRODUCTS_PAGE_SIZE = 50;

const ProductTableScroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function ProductTableScroller(props, ref) {
    const { children, ...rest } = props;
    return (
      <TableContainer component='div'
        ref={ref}
        {...rest}
        sx={{ height: '100%', overflowX: 'auto' }}>
        {children}
      </TableContainer>
    );
  },
);
ProductTableScroller.displayName = 'ProductTableScroller';

const ProductTableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  (props, ref) => <TableBody {...props}
    ref={ref} />,
);
ProductTableBody.displayName = 'ProductTableBody';

const productTableComponents = {
  Scroller: ProductTableScroller,
  Table: (props: HTMLAttributes<HTMLTableElement>) => (
    <Table
      {...props}
      size='small'
      aria-label='product assignment table'
      sx={{ minWidth: 560, tableLayout: 'auto', borderCollapse: 'separate' }}
    />
  ),
  TableRow: (props: HTMLAttributes<HTMLTableRowElement>) => (
    <TableRow {...props}
      hover
      sx={{ height: 56, cursor: 'pointer' }} />
  ),
  TableBody: ProductTableBody,
} as unknown as TableComponents<PromotionProduct>;

const sectionGridSx = {
  px: '10px',
  '& > .MuiGrid-item:first-of-type': { pt: '10px' },
  '& > .MuiGrid-item:last-of-type': { pb: '10px' },
};

// ─── Duration helpers ────────────────────────────────────────────────────────

const minutesToUnit = (minutes: number): { value: number; unit: DurationUnit } => {
  if (minutes > 0 && minutes % 1440 === 0) return { value: minutes / 1440, unit: 'days' };
  if (minutes > 0 && minutes % 60 === 0) return { value: minutes / 60, unit: 'hours' };
  return { value: minutes, unit: 'minutes' };
};

const unitToMinutes = (value: number, unit: DurationUnit): number => {
  if (unit === 'days') return value * 1440;
  if (unit === 'hours') return value * 60;
  return value;
};

// ─── Assignment status helper ────────────────────────────────────────────────

type AssignmentStatusKey = 'assigned' | 'toBeAssigned' | 'toBeUnassigned' | 'available';

const getAssignmentStatusKey = (
  isSelected: boolean,
  wasPersistedAssigned: boolean,
): { key: AssignmentStatusKey; color: 'success' | 'info' | 'warning' | 'default' } => {
  if (isSelected && wasPersistedAssigned) return { key: 'assigned', color: 'success' };
  if (isSelected && !wasPersistedAssigned) return { key: 'toBeAssigned', color: 'info' };
  if (!isSelected && wasPersistedAssigned) return { key: 'toBeUnassigned', color: 'warning' };
  return { key: 'available', color: 'default' };
};

// ─── Form state ──────────────────────────────────────────────────────────────

type PromotionFormState = Omit<PromotionUpsertDto, 'contentIds'>;

const emptyForm: PromotionFormState = {
  name: '',
  companyId: 0,
  customerTypeId: 0,
  catalogueId: 0,
  pricingType: 1,
  discountPercentage: null,
  fixedPromoUnitPrice: null,
  quantityCap: 0,
  startDateUtc: '',
  endDateUtc: '',
  reservationDurationMinutes: 1440,
  isActive: true,
  promoTagId: null,
};

// ─── Component ───────────────────────────────────────────────────────────────

const PromotionEditor = () => {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const theme = useTheme();
  const { promotionId } = useParams<{ promotionId?: string }>();
  const isEdit = Boolean(promotionId);
  const { hasPermissionTo, selectedStore, selectedFranchise } = useContext(UserContext);

  // ── Form ──────────────────────────────────────────────────────────────────
  const [form, setForm] = useState<PromotionFormState>(emptyForm);
  const [selectedContentIds, setSelectedContentIds] = useState<Set<number>>(new Set());
  const [persistedContentIds, setPersistedContentIds] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<ValidationErrors>({});

  // ── Duration UI state ─────────────────────────────────────────────────────
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('days');
  const [durationValue, setDurationValue] = useState<number>(1);

  // ── Option lists ──────────────────────────────────────────────────────────
  const [instances, setInstances] = useState<InstanceOption[]>([]);
  const [markets, setMarkets] = useState<MarketOption[]>([]);
  const [catalogues, setCatalogues] = useState<CatalogueOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [tags, setTags] = useState<ItemTagDto[]>([]);

  // ── Product table ─────────────────────────────────────────────────────────
  const [products, setProducts] = useState<PromotionProduct[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedCatalogue, setSelectedCatalogue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [totalItemPages, setTotalItemPages] = useState(0);
  const [totalItemCount, setTotalItemCount] = useState(0);
  const isLoadingMoreRef = useRef(false);

  // ── Page state ────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [loggedInCompanyId, setLoggedInCompanyId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' | 'warning' } | null>(null);

  const instanceAutoSelectedRef = useRef(false);
  const marketAutoSelectedRef = useRef(false);

  const canWrite = hasPermissionTo(['PromotionWrite']);
  const hasMarketContext = form.companyId > 0 && form.customerTypeId > 0;
  const hasMoreProducts = loadedPageCount < totalItemPages;

  // ── Resolve logged-in company ─────────────────────────────────────────────
  useEffect(() => {
    const storeNumber = selectedStore?.storeNumber?.trim();
    if (!storeNumber) return;
    let isActive = true;
    client.getCompanyIdByStoreNumber(storeNumber)
      .then((result) => { if (isActive && result.success) setLoggedInCompanyId(result.data ?? null); })
      .catch(() => {});
    return () => { isActive = false; };
  }, [selectedStore?.storeNumber]);

  // ── Load instances ────────────────────────────────────────────────────────
  useEffect(() => {
    let isActive = true;
    client.getInstances({ name: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 200 })
      .then((result) => {
        if (isActive) setInstances((result.data?.items ?? []).map((i) => ({ id: i.id, name: i.name })));
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, []);

  // ── Load markets ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!form.companyId) { setMarkets([]); return; }
    let isActive = true;
    client.getMarkets({
      companyId: form.companyId.toString(), name: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 500,
    })
      .then((result) => {
        if (isActive) {
          setMarkets((result.data?.items ?? []).map((m) => ({
            id: m.customerTypeId, name: m.name, companyId: m.companyId,
          })));
        }
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, [form.companyId]);

  // ── Load catalogues ───────────────────────────────────────────────────────
  useEffect(() => {
    if (form.companyId <= 0 || form.customerTypeId <= 0) { setCatalogues([]); return; }
    let isActive = true;
    client.getMarketCatalogues({
      companyId: form.companyId.toString(),
      customerTypeId: form.customerTypeId.toString(),
      searchTerm: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 500,
    })
      .then((result) => {
        if (isActive) setCatalogues((result.data?.items ?? []).map((c) => ({ id: c.customerGroupId, name: c.name })));
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, [form.companyId, form.customerTypeId]);

  // ── Load categories ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedCatalogue || !selectedStore?.storeNumber) { setCategories([]); setSelectedCategory(''); return; }
    let isActive = true;
    client.getCategoriesByCatalogue(selectedStore.storeNumber, selectedCatalogue)
      .then((result) => { if (isActive) setCategories((result.data ?? []).map((c) => ({ id: c.id, name: c.name }))); })
      .catch(() => {});
    return () => { isActive = false; };
  }, [selectedCatalogue, selectedStore?.storeNumber]);

  // ── Load tags ─────────────────────────────────────────────────────────────
  useEffect(() => {
    itemTagsClient.getItemTags({ isActive: true }).then(setTags).catch(() => {});
  }, []);

  // ── Auto-select instance ──────────────────────────────────────────────────
  useEffect(() => {
    if (isEdit || instanceAutoSelectedRef.current || instances.length === 0 || loggedInCompanyId === null) return;
    if (!instances.some((i) => i.id === loggedInCompanyId)) return;
    instanceAutoSelectedRef.current = true;
    setForm((prev) => prev.companyId ? prev : { ...prev, companyId: loggedInCompanyId });
  }, [instances, isEdit, loggedInCompanyId]);

  // ── Auto-select market ────────────────────────────────────────────────────
  useEffect(() => {
    if (isEdit || marketAutoSelectedRef.current || markets.length === 0) return;
    const franchiseName = selectedFranchise?.franchiseName?.trim().toLowerCase();
    if (!franchiseName) return;
    const match = markets.find((m) => m.name.trim().toLowerCase() === franchiseName);
    if (!match) return;
    marketAutoSelectedRef.current = true;
    setForm((prev) => prev.customerTypeId ? prev : { ...prev, customerTypeId: match.id });
  }, [markets, isEdit, selectedFranchise?.franchiseName]);

  const applyPromotionDetail = useCallback((promo: PromotionDto) => {
    setForm({
      name: promo.name,
      companyId: promo.companyId,
      customerTypeId: promo.customerTypeId,
      catalogueId: promo.catalogueId,
      pricingType: promo.pricingType as 1 | 2,
      discountPercentage: promo.discountPercentage,
      fixedPromoUnitPrice: promo.fixedPromoUnitPrice,
      quantityCap: promo.quantityCap,
      startDateUtc: promo.startDateUtc,
      endDateUtc: promo.endDateUtc,
      reservationDurationMinutes: promo.reservationDurationMinutes,
      isActive: promo.isActive,
      promoTagId: promo.promoTagId,
    });

    const parsed = minutesToUnit(promo.reservationDurationMinutes);
    setDurationUnit(parsed.unit);
    setDurationValue(parsed.value);

    const ids = new Set(promo.contentIds ?? []);
    setSelectedContentIds(ids);
    setPersistedContentIds(new Set(ids));

    if (promo.catalogueId) setSelectedCatalogue(promo.catalogueId.toString());
  }, []);

  // ── Load promotion (edit) ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !promotionId) return;
    let isActive = true;
    setIsLoadingDetail(true);
    promotionsClient.getPromotion(parseInt(promotionId, 10))
      .then((promo) => {
        if (!isActive || !promo) return;
        applyPromotionDetail(promo);
      })
      .catch((err: any) => {
        if (isActive) setFeedback({ message: err.message || 'Failed to load promotion.', severity: 'error' });
      })
      .finally(() => { if (isActive) setIsLoadingDetail(false); });
    return () => { isActive = false; };
  }, [applyPromotionDetail, isEdit, promotionId]);

  // ── Load products page 0 ──────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedCatalogue) {
      setProducts([]); setLoadedPageCount(0); setTotalItemPages(0); setTotalItemCount(0);
      isLoadingMoreRef.current = false;
      return;
    }
    let isActive = true;
    setIsLoadingFirstPage(true);
    setProducts([]); setLoadedPageCount(0); setTotalItemPages(0); setTotalItemCount(0);
    isLoadingMoreRef.current = false;

    client.getCatalogueProducts(selectedCatalogue, {
      storeNumber: selectedStore?.storeNumber,
      searchTerm: searchText || '',
      category: selectedCategory || '',
      pageNumber: 0,
      pageSize: PRODUCTS_PAGE_SIZE,
      sort: '',
      direction: 'asc',
    })
      .then((result) => {
        if (!isActive) return;
        const page = result.data;
        setProducts((page?.items?.items ?? []).map((item) => ({
          contentId: item.id, itemNumber: item.sku, productName: item.name, categoryName: item.category,
        })));
        setTotalItemPages(page?.items?.totalPages ?? 0);
        setTotalItemCount(page?.items?.totalCount ?? 0);
        setLoadedPageCount(1);
      })
      .catch((err: any) => { if (isActive) setFeedback({ message: err.message || 'Failed to load products.', severity: 'error' }); })
      .finally(() => { if (isActive) setIsLoadingFirstPage(false); });
    return () => { isActive = false; };
  }, [selectedCatalogue, searchText, selectedCategory, selectedStore?.storeNumber]);

  // ── Infinite scroll ───────────────────────────────────────────────────────
  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMoreRef.current || isLoadingFirstPage || !hasMoreProducts || !selectedCatalogue) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMoreItems(true);
    try {
      const result = await client.getCatalogueProducts(selectedCatalogue, {
        storeNumber: selectedStore?.storeNumber,
        searchTerm: searchText || '',
        category: selectedCategory || '',
        pageNumber: loadedPageCount,
        pageSize: PRODUCTS_PAGE_SIZE,
        sort: '',
        direction: 'asc',
      });
      const page = result.data;
      setProducts((prev) => [
        ...prev,
        ...(page?.items?.items ?? []).map((item) => ({
          contentId: item.id, itemNumber: item.sku, productName: item.name, categoryName: item.category,
        })),
      ]);
      setTotalItemPages(page?.items?.totalPages ?? 0);
      setLoadedPageCount((prev) => prev + 1);
    } catch (err: any) {
      setFeedback({ message: err.message || 'Failed to load products.', severity: 'error' });
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMoreItems(false);
    }
  }, [hasMoreProducts, isLoadingFirstPage, loadedPageCount, selectedCatalogue, selectedStore?.storeNumber, searchText, selectedCategory]);

  // ── Field helpers ─────────────────────────────────────────────────────────

  const updateField = <K extends keyof PromotionFormState>(field: K, value: PromotionFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ValidationErrors]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleInstanceChange = (e: SelectChangeEvent<string>) => {
    const companyId = Number(e.target.value);
    setForm((prev) => ({ ...prev, companyId, customerTypeId: 0, catalogueId: 0 }));
    setSelectedCatalogue(''); setSelectedCategory('');
    setErrors((prev) => ({ ...prev, companyId: undefined, customerTypeId: undefined }));
    marketAutoSelectedRef.current = false;
  };

  const handleMarketChange = (e: SelectChangeEvent<string>) => {
    const customerTypeId = Number(e.target.value);
    const selectedMarket = markets.find((m) => m.id === customerTypeId);
    setForm((prev) => ({ ...prev, companyId: selectedMarket?.companyId ?? prev.companyId, customerTypeId, catalogueId: 0 }));
    setSelectedCatalogue(''); setSelectedCategory('');
    setErrors((prev) => ({ ...prev, companyId: undefined, customerTypeId: undefined }));
  };

  const handleCatalogueChange = (catalogueId: string) => {
    setSelectedCatalogue(catalogueId);
    setSelectedCategory(''); setSearchInput(''); setSearchText('');
    setForm((prev) => ({ ...prev, catalogueId: catalogueId ? Number(catalogueId) : 0 }));
    if (errors.catalogueId) setErrors((prev) => ({ ...prev, catalogueId: undefined }));
  };

  const handleSelectProduct = (contentId: number) => {
    setSelectedContentIds((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) next.delete(contentId);
      else next.add(contentId);
      return next;
    });
    if (errors.contentId) setErrors((prev) => ({ ...prev, contentId: undefined }));
  };

  const handleDurationUnitChange = (_: unknown, newUnit: DurationUnit | null) => {
    if (!newUnit) return;
    setDurationUnit(newUnit);
    const newMinutes = unitToMinutes(durationValue, newUnit);
    updateField('reservationDurationMinutes', newMinutes);
  };

  const handleDurationValueChange = (val: string) => {
    const num = parseInt(val, 10) || 0;
    setDurationValue(num);
    updateField('reservationDurationMinutes', unitToMinutes(num, durationUnit));
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const next: ValidationErrors = {};
    if (!form.name?.trim()) next.name = t('validationName');
    if (selectedContentIds.size === 0 && (!isEdit || persistedContentIds.size === 0)) next.contentId = t('validationProduct');
    if (!form.catalogueId) next.catalogueId = t('validationCatalogue');
    if (!form.companyId) next.companyId = t('validationInstance');
    if (!form.customerTypeId) next.customerTypeId = t('validationMarket');
    if (form.pricingType !== 1 && form.pricingType !== 2) next.pricingType = t('validationPricingType');
    if (form.pricingType === 1 && (!form.discountPercentage || form.discountPercentage <= 0 || form.discountPercentage > 100)) {
      next.discountPercentage = t('validationDiscountPercentage');
    }
    if (form.pricingType === 2 && (!form.fixedPromoUnitPrice || form.fixedPromoUnitPrice <= 0)) {
      next.fixedPromoUnitPrice = t('validationFixedPrice');
    }
    if (!form.quantityCap || form.quantityCap <= 0) next.quantityCap = t('validationQuantityCap');
    if (!form.reservationDurationMinutes || form.reservationDurationMinutes <= 0) {
      next.reservationDurationMinutes = t('validationReservationDuration');
    }
    if (!form.startDateUtc) next.startDateUtc = t('validationStartDate');
    if (!form.endDateUtc) next.endDateUtc = t('validationEndDate');
    if (form.startDateUtc && form.endDateUtc && new Date(form.endDateUtc) <= new Date(form.startDateUtc)) {
      next.endDateUtc = t('validationDateRange');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const dto: PromotionUpsertDto = { ...form, contentIds: Array.from(selectedContentIds) };
      if (isEdit && promotionId) {
        const parsedPromotionId = parseInt(promotionId, 10);
        const updateResult: PromotionUpdateResult = await promotionsClient.updatePromotion(parsedPromotionId, dto);

        if (updateResult.blockedUnassignContentIds.length > 0) {
          const resolvedContentIds = new Set([...dto.contentIds, ...updateResult.blockedUnassignContentIds]);
          setSelectedContentIds(resolvedContentIds);
          setPersistedContentIds(new Set(resolvedContentIds));

          try {
            const refreshedPromotion = await promotionsClient.getPromotion(parsedPromotionId);

            if (refreshedPromotion) {
              applyPromotionDetail(refreshedPromotion);
            }
          } catch {
            // Keep warning visible even if promotion reload fails after partial save.
          }

          setFeedback({
            message: updateResult.message || 'Some products could not be unassigned because they have active promo basket reservations.',
            severity: 'warning',
          });
          return;
        }

        setFeedback({ message: t('updateSuccessMessage'), severity: 'success' });
      } else {
        await promotionsClient.createPromotion(dto);
        setFeedback({ message: t('createSuccessMessage'), severity: 'success' });
      }
      setTimeout(() => navigate('/promotions'), 1500);
    } catch (err: any) {
      setFeedback({ message: err.message || 'Failed to save promotion.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExitAndSave = async () => {
    await handleSave();
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoadingDetail) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      </PageArea>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <PageArea>
      <BackButton
        handleClick={handleExitAndSave}
        title={t('exitAndSave')}
        isLink={false}
        styles={{
          marginBottom: '20px',
          opacity: isSaving ? 0.6 : 1,
          pointerEvents: isSaving ? 'none' : 'auto',
        }}
      />

      <Grid container
        spacing={3}
        alignItems='flex-end'
        sx={{ mb: 3 }}>
        <Grid item
          xs={12}>
          <PageTitle>{isEdit ? t('editorEditTitle') : t('editorTitle')}</PageTitle>
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <Grid container
          spacing={3}
          alignItems='stretch'>

          {/*Left basic info*/}
          <Grid item
            xs={12}
            xl={4}>
            <Section style={{ height: '100%' }}>
              <Box sx={{ p: 3 }}>
                <Grid container
                  spacing={3}
                  sx={sectionGridSx}>

                  {/* Header */}
                  <Grid item
                    xs={12}>
                    <Box>
                      <Typography variant='textLG'
                        fontWeight={600}
                        component='h2'>
                        {t('basicInformation')}
                      </Typography>
                      <Typography variant='textSM'
                        color='text.secondary'>
                        {t('basicInformationSubtitle')}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Name */}
                  <Grid item
                    xs={12}>
                    <TextField
                      fullWidth
                      label={t('nameLabel')}
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      error={Boolean(errors.name)}
                      helperText={errors.name}
                    />
                  </Grid>

                  {/* Instance */}
                  <Grid item
                    xs={12}>
                    <FormControl fullWidth
                      error={Boolean(errors.companyId)}>
                      <InputLabel shrink>{t('instance')}</InputLabel>
                      <Select
                        value={form.companyId ? form.companyId.toString() : ''}
                        label={t('instance')}
                        onChange={handleInstanceChange}
                        displayEmpty
                        notched
                      >
                        <MenuItem value=''>{t('selectInstance')}</MenuItem>
                        {instances.map((inst) => (
                          <MenuItem key={inst.id}
                            value={inst.id.toString()}>{inst.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.companyId && (
                        <Typography variant='textXS'
                          color='error'
                          sx={{ mt: 1 }}>{errors.companyId}</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Market */}
                  <Grid item
                    xs={12}>
                    <FormControl fullWidth
                      error={Boolean(errors.customerTypeId)}>
                      <InputLabel shrink>{t('market')}</InputLabel>
                      <Select
                        value={form.customerTypeId ? form.customerTypeId.toString() : ''}
                        label={t('market')}
                        onChange={handleMarketChange}
                        displayEmpty
                        notched
                        disabled={!form.companyId}
                      >
                        <MenuItem value=''>{t('selectMarket')}</MenuItem>
                        {markets.map((m) => (
                          <MenuItem key={m.id}
                            value={m.id.toString()}>{m.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.customerTypeId && (
                        <Typography variant='textXS'
                          color='error'
                          sx={{ mt: 1 }}>{errors.customerTypeId}</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Catalogue (below market) */}
                  <Grid item
                    xs={12}>
                    <FormControl fullWidth
                      error={Boolean(errors.catalogueId)}>
                      <InputLabel shrink>{t('selectCatalogue')}</InputLabel>
                      <Select
                        value={form.catalogueId ? form.catalogueId.toString() : ''}
                        label={t('selectCatalogue')}
                        displayEmpty
                        notched
                        disabled={!hasMarketContext}
                        onChange={(e: SelectChangeEvent) => handleCatalogueChange(e.target.value)}
                      >
                        <MenuItem value=''>{t('selectCatalogue')}</MenuItem>
                        {catalogues.map((c) => (
                          <MenuItem key={c.id}
                            value={c.id.toString()}>{c.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.catalogueId && (
                        <Typography variant='textXS'
                          color='error'
                          sx={{ mt: 1 }}>{errors.catalogueId}</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Start Date */}
                  <Grid item
                    xs={12}>
                    <DatePickerLocalized
                      label={t('startDateLabel')}
                      value={form.startDateUtc || null}
                      onChange={(date) => updateField('startDateUtc', date ?? '')}
                      useCalendar={false}
                    />
                    {errors.startDateUtc && (
                      <Typography color='error'
                        variant='textXS'
                        sx={{ mt: 0.5, display: 'block' }}>{errors.startDateUtc}</Typography>
                    )}
                  </Grid>

                  {/* End Date */}
                  <Grid item
                    xs={12}>
                    <DatePickerLocalized
                      label={t('endDateLabel')}
                      value={form.endDateUtc || null}
                      onChange={(date) => updateField('endDateUtc', date ?? '')}
                      useCalendar={false}
                    />
                    {errors.endDateUtc && (
                      <Typography color='error'
                        variant='textXS'
                        sx={{ mt: 0.5, display: 'block' }}>{errors.endDateUtc}</Typography>
                    )}
                  </Grid>

                  {/* Pricing Type — simple vertical radio buttons */}
                  <Grid item
                    xs={12}>
                    <Typography variant='textSM'
                      fontWeight={500}
                      sx={{ mb: 1, display: 'block' }}>
                      {t('selectPricingType')}
                    </Typography>
                    <RadioGroup
                      value={form.pricingType.toString()}
                      onChange={(e) => {
                        const newType = parseInt(e.target.value, 10) as 1 | 2;
                        setForm((prev) => ({
                          ...prev,
                          pricingType: newType,
                          discountPercentage: newType === 1 ? prev.discountPercentage : null,
                          fixedPromoUnitPrice: newType === 2 ? prev.fixedPromoUnitPrice : null,
                        }));
                        setErrors((prev) => ({ ...prev, pricingType: undefined, discountPercentage: undefined, fixedPromoUnitPrice: undefined }));
                      }}
                    >
                      <FormControlLabel value='1'
                        control={<Radio size='small' />}
                        label={t('pricingTypePercentage')} />
                      {form.pricingType === 1 && (
                        <Box sx={{ pl: 4, pb: 1 }}>
                          <TextField
                            fullWidth
                            type='number'
                            label={t('discountPercentage')}
                            value={form.discountPercentage ?? ''}
                            onChange={(e) => updateField('discountPercentage', parseFloat(e.target.value) || null)}
                            error={Boolean(errors.discountPercentage)}
                            helperText={errors.discountPercentage}
                            inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                          />
                        </Box>
                      )}
                      <FormControlLabel value='2'
                        control={<Radio size='small' />}
                        label={t('pricingTypeFixed')} />
                      {form.pricingType === 2 && (
                        <Box sx={{ pl: 4, pb: 1 }}>
                          <TextField
                            fullWidth
                            type='number'
                            label={t('fixedPromoUnitPrice')}
                            value={form.fixedPromoUnitPrice ?? ''}
                            onChange={(e) => updateField('fixedPromoUnitPrice', parseFloat(e.target.value) || null)}
                            error={Boolean(errors.fixedPromoUnitPrice)}
                            helperText={errors.fixedPromoUnitPrice}
                            inputProps={{ min: 0.01, step: 0.01 }}
                          />
                        </Box>
                      )}
                    </RadioGroup>
                    {errors.pricingType && (
                      <Typography color='error'
                        variant='textXS'
                        sx={{ mt: 0.5, display: 'block' }}>{errors.pricingType}</Typography>
                    )}
                  </Grid>

                  {/* Quantity Cap */}
                  <Grid item
                    xs={12}
                    sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      type='number'
                      label={t('quantityCap')}
                      value={form.quantityCap || ''}
                      onChange={(e) => updateField('quantityCap', parseInt(e.target.value, 10) || 0)}
                      error={Boolean(errors.quantityCap)}
                      helperText={errors.quantityCap || t('quantityCapHint')}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  {/* Reservation Duration — horizontal number + unit select */}
                  <Grid item
                    xs={12}>
                    <Grid container
                      spacing={1}
                      alignItems='flex-start'>
                      <Grid item
                        xs={6}>
                        <TextField
                          fullWidth
                          type='number'
                          label={t('reservationDuration')}
                          value={durationValue || ''}
                          onChange={(e) => handleDurationValueChange(e.target.value)}
                          error={Boolean(errors.reservationDurationMinutes)}
                          helperText={errors.reservationDurationMinutes || t('reservationDurationHint')}
                          inputProps={{ min: 1 }}
                          FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                        />
                      </Grid>
                      <Grid item
                        xs={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink>{t('durationUnit')}</InputLabel>
                          <Select
                            value={durationUnit}
                            label={t('durationUnit')}
                            notched
                            onChange={(e: SelectChangeEvent) => handleDurationUnitChange(null, e.target.value as DurationUnit)}
                          >
                            <MenuItem value='minutes'>{t('durationMinutes')}</MenuItem>
                            <MenuItem value='hours'>{t('durationHours')}</MenuItem>
                            <MenuItem value='days'>{t('durationDays')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Promo Tag */}
                  <Grid item
                    xs={12}
                    sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel shrink>{t('selectTag')}</InputLabel>
                      <Select
                        value={form.promoTagId?.toString() ?? ''}
                        label={t('selectTag')}
                        displayEmpty
                        notched
                        onChange={(e: SelectChangeEvent) => {
                          const val = e.target.value;
                          updateField('promoTagId', val ? parseInt(val, 10) : null);
                        }}
                      >
                        <MenuItem value=''>{t('noTag')}</MenuItem>
                        {tags.map((tag) => (
                          <MenuItem key={tag.id}
                            value={tag.id.toString()}>{tag.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Active Toggle */}
                  <Grid item
                    xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={form.isActive}
                          onChange={(e) => updateField('isActive', e.target.checked)} />}
                        label={t('activeToggle')}
                      />
                      <Typography variant='textXS'
                        color='text.secondary'>{t('activeToggleHint')}</Typography>
                    </Box>
                  </Grid>

                </Grid>
              </Box>
            </Section>
          </Grid>

          {/* ═══════════════════════════════════════════════════════════════
              RIGHT — Product Assignment
          ═══════════════════════════════════════════════════════════════ */}
          <Grid item
            xs={12}
            xl={8}>
            <Section style={{ height: '100%' }}>
              <Box sx={{ p: 3 }}>
                <Grid container
                  spacing={3}
                  sx={sectionGridSx}>

                  <Grid item
                    xs={12}>
                    <Box>
                      <Typography variant='textLG'
                        fontWeight={600}
                        component='h2'>
                        {t('productAssignmentTitle')}
                      </Typography>
                      <Typography variant='textSM'
                        color='text.secondary'>
                        {t('productAssignmentSubtitle')}
                      </Typography>
                    </Box>
                  </Grid>

                  {!hasMarketContext || !form.catalogueId ? (
                    <Grid item
                      xs={12}>
                      <Alert severity='info'>{t('selectInstanceAndMarketFirst')}</Alert>
                    </Grid>
                  ) : (
                    <>
                      {/* Search + Category */}
                      <Grid item
                        xs={12}
                        md={6}>
                        <TextField
                          fullWidth
                          label={t('search')}
                          placeholder={t('assignmentSearchPlaceholder')}
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setSearchText(searchInput.trim()); }}
                        />
                      </Grid>
                      <Grid item
                        xs={12}
                        md={6}>
                        <FormControl fullWidth>
                          <InputLabel shrink>{t('filterByCategoryLabel')}</InputLabel>
                          <Select
                            value={selectedCategory}
                            label={t('filterByCategoryLabel')}
                            displayEmpty
                            notched
                            onChange={(e: SelectChangeEvent) => setSelectedCategory(e.target.value)}
                          >
                            <MenuItem value=''>{t('allCategories')}</MenuItem>
                            {categories.map((c) => (
                              <MenuItem key={c.id}
                                value={c.id.toString()}>{c.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Clear + Search */}
                      <Grid item
                        xs={12}>
                        <Grid container
                          spacing={3}
                          justifyContent='flex-end'>
                          <Grid item
                            lg={2}
                            md={3}
                            sm={4}
                            xs={12}>
                            <Button
                              variant='secondary'
                              size='lg'
                              fullWidth
                              onClick={() => { setSearchInput(''); setSearchText(''); setSelectedCategory(''); }}
                            >
                              {t('clear')}
                            </Button>
                          </Grid>
                          <Grid item
                            lg={2}
                            md={3}
                            sm={4}
                            xs={12}>
                            <Button
                              variant='primary'
                              size='lg'
                              fullWidth
                              startIcon={<SvgIcon><Search /></SvgIcon>}
                              onClick={() => setSearchText(searchInput.trim())}
                            >
                              {t('search')}
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Product table */}
                      {isLoadingFirstPage ? (
                        <Grid item
                          xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                        </Grid>
                      ) : products.length === 0 ? (
                        <Grid item
                          xs={12}>
                          <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography variant='textMD'
                              color='text.secondary'>{t('assignmentEmptyState')}</Typography>
                          </Box>
                        </Grid>
                      ) : (
                        <Grid item
                          xs={12}>
                          {errors.contentId && (
                            <Typography variant='textXS'
                              color='error'
                              sx={{ mb: 1, display: 'block' }}>{errors.contentId}</Typography>
                          )}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: errors.contentId ? 'error.main' : 'divider',
                              borderRadius: 3,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                px: 3, py: 2,
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', md: 'center' },
                                gap: 2, flexDirection: { xs: 'column', md: 'row' },
                                borderBottom: '1px solid', borderColor: 'divider',
                              }}
                            >
                              <Typography variant='textMD'
                                fontWeight={600}>{t('selectProduct')}</Typography>
                              {totalItemCount > 0 && (
                                <Typography variant='textSM'
                                  color='text.secondary'>
                                  {t('showingItems', { shown: products.length, total: totalItemCount })}
                                </Typography>
                              )}
                            </Box>

                            <TableVirtuoso
                              data={products}
                              computeItemKey={(_, item) => item.contentId}
                              components={productTableComponents}
                              overscan={20}
                              endReached={loadMoreProducts}
                              style={{ height: Math.min(Math.max(products.length * 56 + 56, 300), 750) }}
                              fixedFooterContent={isLoadingMoreItems ? () => (
                                <TableRow>
                                  <TableCell colSpan={5}
                                    align='center'
                                    sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
                                    <CircularProgress size={20} />
                                  </TableCell>
                                </TableRow>
                              ) : null}
                              fixedHeaderContent={() => (
                                <TableRow>
                                  <TableCell padding='checkbox'
                                    sx={{ width: 72, minWidth: 72, maxWidth: 72, pl: 2, pr: 1, position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.custom.gray[100], fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }} />
                                  <TableCell sx={{ width: '20%', minWidth: 160, position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.custom.gray[100], fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{t('sku')}</TableCell>
                                  <TableCell sx={{ minWidth: 280, position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.custom.gray[100], fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{t('productName')}</TableCell>
                                  <TableCell sx={{ width: '22%', minWidth: 180, position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.custom.gray[100], fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{t('category')}</TableCell>
                                  <TableCell sx={{ width: '18%', minWidth: 140, position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.custom.gray[100], fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{t('status')}</TableCell>
                                </TableRow>
                              )}
                              itemContent={(_, item) => {
                                const isAssigned = selectedContentIds.has(item.contentId);
                                const wasAssigned = persistedContentIds.has(item.contentId);
                                const status = getAssignmentStatusKey(isAssigned, wasAssigned);
                                const statusLabelMap: Record<AssignmentStatusKey, string> = {
                                  assigned: t('assignmentStatusAssigned'),
                                  toBeAssigned: t('assignmentStatusToBeAssigned'),
                                  toBeUnassigned: t('assignmentStatusToBeUnassigned'),
                                  available: t('assignmentStatusAvailable'),
                                };
                                return (
                                  <>
                                    <TableCell padding='checkbox'
                                      sx={{ width: 72, minWidth: 72, maxWidth: 72, pl: 2, pr: 1 }}>
                                      <Checkbox
                                        checked={isAssigned}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={() => handleSelectProduct(item.contentId)}
                                        inputProps={{ 'aria-label': `${t('selectProduct')} ${item.productName}` }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ width: '20%', minWidth: 160, whiteSpace: 'nowrap' }}
                                      onClick={() => handleSelectProduct(item.contentId)}>{item.itemNumber}</TableCell>
                                    <TableCell sx={{ minWidth: 280 }}
                                      onClick={() => handleSelectProduct(item.contentId)}>{item.productName}</TableCell>
                                    <TableCell sx={{ width: '22%', minWidth: 180 }}
                                      onClick={() => handleSelectProduct(item.contentId)}>{item.categoryName}</TableCell>
                                    <TableCell sx={{ width: '18%', minWidth: 140 }}>
                                      <Chip label={statusLabelMap[status.key]}
                                        color={status.color}
                                        size='small' />
                                    </TableCell>
                                  </>
                                );
                              }}
                            />
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}

                </Grid>
              </Box>
            </Section>
          </Grid>
        </Grid>

        {/* Save / Cancel */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button variant='secondary'
            size='lg'
            onClick={() => navigate('/promotions')}
            disabled={isSaving}
            sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {t('cancel')}
          </Button>
          <Button variant='primary'
            size='lg'
            onClick={handleSave}
            disabled={isSaving || !canWrite}
            sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {isSaving ? t('saving') : t('save')}
          </Button>
        </Box>
      </Stack>

      {feedback && (
        <MessagesSnackbar open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000} />
      )}
    </PageArea>
  );
};

export default PromotionEditor;
